import type { ReactNode } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "../../../components/icons";
import { buildPageNumbers } from "../../../lib/pagination/buildPageNumbers";
import type { PaginatedMeta } from "../../../types/formList";

interface FormsListPaginationProps {
  meta: PaginatedMeta | null;
  page: number;
  perPage: number;
  perPageOptions: readonly number[];
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

function getRangeLabel(meta: PaginatedMeta): string {
  const from = meta.from ?? (meta.current_page - 1) * meta.per_page + 1;
  const to = meta.to ?? Math.min(meta.current_page * meta.per_page, meta.total);

  return `${from}–${to} / ${meta.total}`;
}

function NavButton({
  title,
  disabled,
  onClick,
  children,
}: {
  title: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={[
        "inline-flex h-7 w-7 items-center justify-center rounded-md border text-slate-600 transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
        disabled
          ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function PageNumberButton({
  page,
  isActive,
  disabled,
  onClick,
}: {
  page: number;
  isActive: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Страница ${page}`}
      aria-current={isActive ? "page" : undefined}
      disabled={disabled}
      onClick={onClick}
      className={[
        "inline-flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 font-onest text-xs font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
        isActive
          ? "bg-brand-500 text-white shadow-sm"
          : disabled
            ? "cursor-not-allowed text-slate-300"
            : "text-slate-600 hover:bg-slate-100",
      ].join(" ")}
    >
      {page}
    </button>
  );
}

export function FormsListPagination({
  meta,
  page,
  perPage,
  perPageOptions,
  isLoading,
  onPageChange,
  onPerPageChange,
}: FormsListPaginationProps) {
  if (!meta || meta.total === 0) return null;

  const lastPage = meta.last_page;
  const hasMultiplePages = lastPage > 1;
  const pageItems = buildPageNumbers(page, lastPage);

  return (
    <div
      className={[
        "flex flex-col gap-2 border-t border-slate-100 bg-slate-50/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-4",
        isLoading ? "pointer-events-none opacity-60" : "",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 font-onest text-xs text-slate-500">
        <span className="font-medium text-slate-700">
          {getRangeLabel(meta)}
        </span>
        <span className="text-slate-300">|</span>
        <label className="flex items-center gap-1.5">
          <span className="hidden sm:inline">По</span>
          <select
            value={perPage}
            disabled={isLoading}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            className="h-7 min-w-[3.25rem] cursor-pointer rounded-md border border-slate-200 bg-white py-0 pl-2 pr-8 text-xs leading-7 text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            {perPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      {hasMultiplePages ? (
        <nav
          aria-label="Пагинация списка форм"
          className="flex items-center justify-center gap-0.5 sm:justify-end"
        >
          <NavButton
            title="Первая"
            disabled={isLoading || page <= 1}
            onClick={() => onPageChange(1)}
          >
            <IconChevronsLeft size={14} />
          </NavButton>
          <NavButton
            title="Назад"
            disabled={isLoading || page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <IconChevronLeft size={14} />
          </NavButton>

          <div className="mx-0.5 flex items-center gap-0.5">
            {pageItems.map((item, index) =>
              item === "ellipsis" ? (
                <span
                  key={`ellipsis-${index}`}
                  className="inline-flex h-7 w-6 items-center justify-center text-xs text-slate-400"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <PageNumberButton
                  key={item}
                  page={item}
                  isActive={item === page}
                  disabled={isLoading}
                  onClick={() => onPageChange(item)}
                />
              ),
            )}
          </div>

          <NavButton
            title="Вперёд"
            disabled={isLoading || page >= lastPage}
            onClick={() => onPageChange(page + 1)}
          >
            <IconChevronRight size={14} />
          </NavButton>
          <NavButton
            title="Последняя"
            disabled={isLoading || page >= lastPage}
            onClick={() => onPageChange(lastPage)}
          >
            <IconChevronsRight size={14} />
          </NavButton>
        </nav>
      ) : null}
    </div>
  );
}
